import { AreaBounds } from "cloud-core/spatial/Spatial";
import { Milliseconds } from "../../utilities/numerics";
import { MediaSource } from "../media/MediaSource";
import { DataDescription } from "./dataDescriptions/DataDescription";
import { EntityProperties } from "./dataDescriptions/EntityProperties";
import { EntityProperty } from "./entityProperties/EntityProperty";
import { NumberListEntityProperty } from "./entityProperties/NumberListEntityProperty";
import { DateEntityProperty } from "./entityProperties/DateEntityProperty";

export interface EntitySourceObject {
    id: string;
    object: unknown;
    projectId: string;
    mediaSource: MediaSource | undefined;
    mediaSourceFrameOffset: number | undefined;
    runId: number | undefined;
}

export class Entity {
    id: string;
    properties: { [key: string]: EntityProperty<unknown> };
    sourceObject: EntitySourceObject;
    dataDescription: DataDescription;
    images: EntityImageCollection;

    constructor(init: Partial<Entity>) {
        Object.assign(this, init);
    }

    getTitle(): string {
        return this.dataDescription.infoGenerator.titleGenerator.generateInformation(this);
    }

    getSubtitle(): string {
        return this.dataDescription.infoGenerator.subtitleGenerator.generateInformation(this);
    }

    getStartTimeOr(startTime: Date): Date {
        const property = this.properties[EntityProperties.StartTime.key];

        if (property === undefined) {
            return startTime;
        }

        return (property as DateEntityProperty).value();
    }

    getEndTimeOr(endTime: Date): Date {
        const property = this.properties[EntityProperties.EndTime.key];

        if (property === undefined) {
            return endTime;
        }

        return (property as DateEntityProperty).value();
    }

    getBoundsOr(bounds: AreaBounds): AreaBounds {
        const property = this.properties[EntityProperties.Meta.Bounds.key];

        if (property === undefined) {
            return bounds;
        }

        const propertyBounds = (property as NumberListEntityProperty).value() as AreaBounds;

        if (propertyBounds.length !== 4) {
            return bounds;
        }

        return propertyBounds;
    }
}

export class FileVideoFrameEntityImage {
    origin = "videoFileFrame" as const;
    timestamp: Milliseconds;
    bounds: AreaBounds;
    fileId: string;
    thumbnailUrl: string;

    public hasImage(): boolean {
        return this.thumbnailUrl !== undefined || this.fileId !== undefined;
    }

    constructor(init: string | { timestamp: Milliseconds; bounds: AreaBounds; fileId: string }) {
        if (typeof init === "string") {
            this.thumbnailUrl = init;
        } else {
            Object.assign(this, init);
        }
    }
}

export class FileImageEntityImage {
    origin = "imageFile" as const;
    fileId: string;

    public hasImage(): boolean {
        return this.fileId !== undefined;
    }

    constructor(init: Partial<FileImageEntityImage>) {
        Object.assign(this, init);
    }
}

export class SourceImageEntityImage {
    origin = "imageSrc" as const;
    sourceUrl: string;

    constructor(init: Partial<FileImageEntityImage>) {
        Object.assign(this, init);
    }
}

export type EntityImage = FileVideoFrameEntityImage | FileImageEntityImage;

export class EntityImageCollection {
    images: EntityImage[];
    mainImageSelector: (images: EntityImageCollection) => EntityImage;

    constructor(images: EntityImage[] = []) {
        this.images = images;
        this.mainImageSelector = (imageCollection: EntityImageCollection) => {
            return imageCollection.images[0];
        };
    }

    getMainImage(): EntityImage {
        return this.mainImageSelector(this);
    }
}
